/** Backend max items per bulk-remove / bulk-import request */
export const SCHOOL_USERS_BULK_CHUNK_SIZE = 50;

export function chunkArray(items, chunkSize = SCHOOL_USERS_BULK_CHUNK_SIZE) {
  if (!items?.length) return [];
  const chunks = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
}

function normalizeEmail(value) {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * Pack rows into chunks ≤ chunkSize without splitting a parent from their students.
 * Parent row is always placed before its children in the same chunk when possible.
 */
export function chunkImportRowsByFamily(rows, chunkSize = SCHOOL_USERS_BULK_CHUNK_SIZE) {
  if (!rows?.length) return [];

  const parentByEmail = new Map();
  for (const row of rows) {
    if (row.type === 'parent') {
      const email = normalizeEmail(row.email);
      if (email && !parentByEmail.has(email)) {
        parentByEmail.set(email, row);
      }
    }
  }

  const childrenByParentEmail = new Map();
  for (const row of rows) {
    if (row.type !== 'student') continue;
    const parentEmail = normalizeEmail(row.parent_email);
    if (!parentEmail) continue;
    if (!childrenByParentEmail.has(parentEmail)) {
      childrenByParentEmail.set(parentEmail, []);
    }
    childrenByParentEmail.get(parentEmail).push(row);
  }

  const units = [];
  const usedRowIndexes = new Set();

  const markUsed = (row) => {
    if (row?.row_index != null) usedRowIndexes.add(row.row_index);
  };

  // Families: first time we see each parent in file order
  const seenParentEmails = new Set();
  for (const row of rows) {
    if (row.type !== 'parent') continue;
    const email = normalizeEmail(row.email);
    if (!email || seenParentEmails.has(email)) continue;
    seenParentEmails.add(email);

    const parentRow = parentByEmail.get(email);
    const children = childrenByParentEmail.get(email) || [];
    units.push({ kind: 'family', rows: [parentRow, ...children] });
    markUsed(parentRow);
    children.forEach(markUsed);
  }

  // Everyone else in original order (standalone students, teachers, admins)
  for (const row of rows) {
    if (row?.row_index != null && usedRowIndexes.has(row.row_index)) continue;
    if (row.type === 'parent') continue;
    units.push({ kind: 'single', rows: [row] });
    markUsed(row);
  }

  const chunks = [];
  let current = [];

  const flush = () => {
    if (current.length) {
      chunks.push(current);
      current = [];
    }
  };

  for (const unit of units) {
    const unitRows = unit.rows;
    const size = unitRows.length;

    if (size > chunkSize) {
      flush();
      const [parent, ...children] = unitRows;
      if (parent?.type === 'parent') {
        chunks.push([parent]);
        for (let i = 0; i < children.length; i += chunkSize) {
          chunks.push(children.slice(i, i + chunkSize));
        }
      } else {
        for (let i = 0; i < unitRows.length; i += chunkSize) {
          chunks.push(unitRows.slice(i, i + chunkSize));
        }
      }
      continue;
    }

    if (current.length + size > chunkSize) flush();
    current.push(...unitRows);
  }

  flush();
  return dedupeParentRowsAcrossChunks(chunks);
}

/**
 * Never send the same parent row twice (chunk 2+ only students/staff; parent created in chunk 1).
 */
export function dedupeParentRowsAcrossChunks(chunks) {
  const importedParents = new Set();

  return chunks.map((chunk) => {
    const out = [];
    for (const row of chunk) {
      if (row.type === 'parent') {
        const email = normalizeEmail(row.email);
        if (!email) continue;
        if (importedParents.has(email)) continue;
        importedParents.add(email);
        out.push(row);
      } else {
        out.push(row);
      }
    }
    return out;
  });
}

export function isSchoolBulkTooLargeError(err) {
  const code = err?.data?.code || err?.data?.error || err?.message;
  return code === 'SCHOOL_BULK_TOO_LARGE';
}
